import React, { useState, useCallback } from 'react';
import { X, FolderPlus, Folder } from 'lucide-react';
import PopupTransition from '@/components/ui/PopupTransition';

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFolder: (folderName: string) => void;
  currentPath: string;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ isOpen, onClose, onCreateFolder, currentPath }) => {
  const [folderName, setFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!folderName.trim()) return;

      setIsCreating(true);

      try {
        onCreateFolder(folderName.trim());
        setFolderName('');
        onClose();
      } catch (error) {
        console.error('Error creating folder:', error);
      } finally {
        setIsCreating(false);
      }
    },
    [folderName, onCreateFolder, onClose],
  );

  const handleClose = useCallback(() => {
    setFolderName('');
    onClose();
  }, [onClose]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <PopupTransition open={isOpen} onClose={onClose}>
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl bg-white/20 p-3 backdrop-blur-sm">
                <FolderPlus className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Δημιουργία νέου φακέλου</h3>
                <p className="mt-1 text-sm text-blue-100">
                  Δημιουργήστε έναν νέο φάκελο για την οργάνωση των αρχείων σας
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="rounded-xl p-2 text-white/80 transition-all duration-200 hover:bg-white/20 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Subtitle */}
        <div className="border-b border-gray-100 bg-blue-50/50 px-8 py-4">
          <div className="flex items-center gap-3">
            <Folder className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Τοποθεσία: {currentPath === '/' ? 'Root Directory' : currentPath}
            </span>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-8">
          <div className="space-y-6">
            <div>
              <label htmlFor="folderName" className="mb-3 block text-sm font-semibold text-gray-800">
                Όνομα φακέλου
              </label>
              <input
                id="folderName"
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="π.χ. Έγγραφα, Εικόνες, Αρχεία έργου..."
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none"
                autoFocus
                disabled={isCreating}
              />
              <p className="mt-2 text-xs text-gray-500">
                Επιλέξτε ένα περιγραφικό όνομα που θα σας βοηθήσει να οργανώσετε τα αρχεία σας
              </p>
            </div>

            {folderName.trim() && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-blue-100 p-2">
                    <Folder className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Προεπισκόπηση φακέλου</p>
                    <p className="text-sm text-blue-700">
                      {currentPath === '/' ? '' : currentPath}/
                      <span className="font-semibold">{folderName.trim()}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="rounded-xl border-2 border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 focus:outline-none disabled:opacity-50"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              disabled={!folderName.trim() || isCreating}
              className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg focus:ring-4 focus:ring-blue-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none"
            >
              {isCreating ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Δημιουργία...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FolderPlus className="h-4 w-4" />
                  Δημιουργία φακέλου
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </PopupTransition>
  );
};

export default CreateFolderModal;
